import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FmeaStepProgress.module.css';

interface FmeaStepProgressProps {
  currentStep: number;
  totalSteps?: number;
}

const DEFAULT_TOTAL_STEPS = 7;

const stepDefinitions = [
  { headerKey: 'step1PlanningPreparation', progressKey: 'progressSteps.planning' },
  { headerKey: 'step2StructureAnalysis', progressKey: 'progressSteps.structure' },
  { headerKey: 'step3FunctionAnalysis', progressKey: 'progressSteps.functions' },
  { headerKey: 'step4FailureAnalysis', progressKey: 'progressSteps.failures' },
  { headerKey: 'step5RiskAnalysis', progressKey: 'progressSteps.risks' },
  { headerKey: 'step6Optimization', progressKey: 'progressSteps.optimization' },
  { headerKey: 'step7FmeaReports', progressKey: 'progressSteps.reports' },
];

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const FmeaStepProgress: React.FC<FmeaStepProgressProps> = ({ currentStep, totalSteps = DEFAULT_TOTAL_STEPS }) => {
  const { t } = useTranslation('form');

  const safeTotalSteps = clamp(totalSteps, 1, stepDefinitions.length);
  const safeCurrentStep = clamp(currentStep, 1, safeTotalSteps);

  const percentComplete = useMemo(() => {
    const raw = (safeCurrentStep / safeTotalSteps) * 100;
    return Math.min(100, Math.max(0, Math.floor(raw)));
  }, [safeCurrentStep, safeTotalSteps]);

  const { headerLabel, progressStops, segmentInfo } = useMemo(() => {
    const label = stepDefinitions[safeCurrentStep - 1]
      ? t(stepDefinitions[safeCurrentStep - 1].headerKey)
      : '';

    if (safeTotalSteps === 1) {
      const singleStop = stepDefinitions[0];
      return {
        headerLabel: label,
        progressStops: [
          {
            key: singleStop.progressKey,
            label: t(singleStop.progressKey),
            percent: 50,
          },
        ],
        segmentInfo: { segment: 100, margin: 0 },
      };
    }

    const segment = 100 / (safeTotalSteps - 1);
    const margin = segment / 2;

    const stops = stepDefinitions.slice(0, safeTotalSteps).map((step, index) => ({
      key: step.progressKey,
      label: t(step.progressKey),
      percent: margin + index * segment,
    }));

    return { headerLabel: label, progressStops: stops, segmentInfo: { segment, margin } };
  }, [safeCurrentStep, safeTotalSteps, t]);

  const fillPercent = useMemo(() => {
    if (safeTotalSteps === 1) return 50;
    if (!progressStops.length) return percentComplete;
    const { segment = 0, margin = 0 } = segmentInfo || {};
    const stepIndex = Math.min(Math.max(safeCurrentStep - 1, 0), progressStops.length - 1);
    return margin + stepIndex * segment;
  }, [percentComplete, progressStops, safeCurrentStep, safeTotalSteps, segmentInfo]);

  const progressLabel = t('stepperProgressLabel', {
    current: safeCurrentStep,
    total: safeTotalSteps,
    label: headerLabel,
  });

  const percentLabel = t('stepperProgressPercent', {
    percent: percentComplete,
  });

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <span className={styles.headerText}>{progressLabel}</span>
        <span className={styles.percentText}>{percentLabel}</span>
      </div>
      <div className={styles.barWrapper}>
        <div className={styles.barTrack} />
        <div className={styles.barFill} style={{ width: `${fillPercent}%` }} />
        <div className={styles.markerRow}>
          {progressStops.map((stop, index) => {
            const isActive = index + 1 <= safeCurrentStep;
            return (
              <div
                key={stop.key}
                className={styles.marker}
                style={{ left: `${stop.percent}%` }}
              >
                <span
                  className={`${styles.markerDot} ${isActive ? styles.markerDotActive : ''}`.trim()}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.labelRow}>
        {progressStops.map(stop => (
          <span
            key={stop.key}
            className={styles.label}
            style={{ left: `${stop.percent}%` }}
          >
            {stop.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default FmeaStepProgress;
